export type SQDCBoardSelectVariables = Partial<{
  ids: number[] | number;
  siteIds: number[] | number;
  onlySiteWithPermission: boolean;
}>;

export type SQDCBoardSelectItem = {
  id: number;
  siteId: number;
  allowedUsersConnection?: readonly { userId: number }[];
};

export type SQDCBoardSelectDisabledNode = {
  key: string | number;
  includeChildren: boolean;
};

/** Restrict a board query to the intersection of its site filter and an effective right grant. */
export function restrictBoardSelectVariablesToSiteIds(
  variables: SQDCBoardSelectVariables,
  allowedSiteIds: number[] | null,
): SQDCBoardSelectVariables {
  if (allowedSiteIds == null) {
    return variables;
  }

  const requestedSiteIds =
    variables.siteIds == null
      ? allowedSiteIds
      : Array.isArray(variables.siteIds)
        ? variables.siteIds
        : [variables.siteIds];
  const siteIds = requestedSiteIds.filter((siteId) => allowedSiteIds.includes(siteId));

  // The SQDCBoards resolver treats an empty array as no filter, so use an impossible site id.
  return { ...variables, siteIds: siteIds.length > 0 ? siteIds : [-1] };
}

/** Keep boards visible while preventing selection outside the effective site grant. */
export function getBoardsDisabledWithoutAccess(
  boards: SQDCBoardSelectItem[],
  allowedSiteIds: number[] | null,
  currentUserId?: number,
  canBypassAllowlist = false,
): SQDCBoardSelectDisabledNode[] {
  return boards
    .filter((board) => {
      const lacksSiteRight = allowedSiteIds != null && !allowedSiteIds.includes(board.siteId);
      const allowedUsers = board.allowedUsersConnection ?? [];
      const excludedByAllowlist =
        !canBypassAllowlist &&
        allowedUsers.length > 0 &&
        !allowedUsers.some(({ userId }) => userId === currentUserId);
      return lacksSiteRight || excludedByAllowlist;
    })
    .map((board) => ({ key: board.id, includeChildren: false }));
}
